import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import { GET_SLA, CREAR_SLA, ACTUALIZAR_SLA } from './gql.js';

const TIPOS = [
  { value: 'URBANO', label: 'Urbano' },
  { value: 'ZONAS', label: 'Zonas' },
  { value: 'NACIONAL', label: 'Nacional' },
  { value: 'EXPRESS', label: 'Express' },
  { value: 'SAME_DAY', label: 'Same day' },
];

const FORM_ID = 'sla-form';

export default function SlaForm({ slaId, onCancel, onSaved }) {
  const isEdit = Boolean(slaId);
  const [{ data }] = useQuery({ query: GET_SLA, variables: { id: slaId }, pause: !isEdit });
  const [, crearSla] = useMutation(CREAR_SLA);
  const [, actualizarSla] = useMutation(ACTUALIZAR_SLA);

  const [form, setForm] = useState({
    nombre: '',
    tipoServicio: 'URBANO',
    zona: '',
    promesaHoras: 24,
    primeraRespuestaMin: 120,
    activo: true,
  });

  useEffect(() => {
    if (data?.getSLA) {
      const sla = data.getSLA;
      setForm({
        nombre: sla.nombre,
        tipoServicio: sla.tipoServicio,
        zona: sla.zona || '',
        promesaHoras: sla.promesaHoras,
        primeraRespuestaMin: sla.primeraRespuestaMin,
        activo: sla.activo,
      });
    }
  }, [data]);

  const handleChange = (field) => (event) => {
    const value = field === 'activo' ? event.target.checked : event.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const input = {
      nombre: form.nombre,
      tipoServicio: form.tipoServicio,
      zona: form.zona || null,
      promesaHoras: Number(form.promesaHoras),
      primeraRespuestaMin: Number(form.primeraRespuestaMin),
      activo: Boolean(form.activo),
    };
    const result = isEdit
      ? await actualizarSla({ id: slaId, input })
      : await crearSla({ input });
    if (result.error) {
      alert(result.error.message);
      return;
    }
    onSaved?.(result.data?.crearSLA?.id ?? slaId);
  };

  return (
    <div className="w-full">
      <PageHeader title={isEdit ? 'Editar SLA' : 'Nuevo SLA'} subtitle="Definí promesas y tiempos objetivo" />
      <form id={FORM_ID} onSubmit={handleSubmit} className="mx-auto mt-6 grid w-full max-w-[900px] grid-cols-12 gap-6">
        <section className="col-span-12 space-y-5 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl">
          <Field label="Nombre" htmlFor="nombre">
            <input
              id="nombre"
              value={form.nombre}
              onChange={handleChange('nombre')}
              required
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 sm:col-span-6">
              <Field label="Tipo de servicio" htmlFor="tipoServicio">
                <select
                  id="tipoServicio"
                  value={form.tipoServicio}
                  onChange={handleChange('tipoServicio')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {TIPOS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="Zona" htmlFor="zona" hint="Dejar vacío para aplicar globalmente">
                <input
                  id="zona"
                  value={form.zona}
                  onChange={handleChange('zona')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="Promesa (horas)" htmlFor="promesaHoras">
                <input
                  id="promesaHoras"
                  type="number"
                  value={form.promesaHoras}
                  min={1}
                  onChange={handleChange('promesaHoras')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="1ª respuesta (minutos)" htmlFor="primeraRespuesta">
                <input
                  id="primeraRespuesta"
                  type="number"
                  value={form.primeraRespuestaMin}
                  min={1}
                  onChange={handleChange('primeraRespuestaMin')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
            <input
              type="checkbox"
              checked={form.activo}
              onChange={handleChange('activo')}
              className="h-4 w-4 rounded border-[rgba(var(--border),0.6)] text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
            Activo
          </label>
        </section>
      </form>

      <StickyActions>
        <div className="flex w-full items-center justify-between gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgba(var(--border),0.6)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form={FORM_ID}
            className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Guardar
          </button>
        </div>
      </StickyActions>
    </div>
  );
}
